import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { MaterialModule } from '../../../../../material.module';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgIf, NgFor, DatePipe } from '@angular/common';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LocalizationPipe } from '../../../../../pipe/localization-pipe';
import { LocalizationService } from '../../../../../services/localizationService';
import { MatDialog } from '@angular/material/dialog';
import { DeleteConfirmationDialogComponent, DeleteConfirmationData } from '../../resource-management/delete-confirmation-dialog.component';
import * as XLSX from 'xlsx';

export interface QuestionAnswer {
  id?: string;
  question: string;
  answer: string;
  isActive: boolean;
  tags?: string[];
  createdAt: Date;
}

@Component({
  selector: 'app-qa-input',
  standalone: true,
  imports: [
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgFor,
    DatePipe,
    TablerIconsModule,
    MatProgressSpinnerModule,
    LocalizationPipe
  ],
  templateUrl: './qa-input.component.html',
  styleUrls: ['./qa-input.component.scss'],
})
export class QaInputComponent implements OnInit {
  @Input() formGroup: FormGroup;
  @Output() qaDataChange = new EventEmitter<QuestionAnswer[]>();
  @Output() fileUploadEvent = new EventEmitter<File>();

  qaList: QuestionAnswer[] = [];
  filteredQaList: QuestionAnswer[] = [];
  searchQuery = '';
  showImportModal = false;
  tags: string[] = [];
  tagInput = '';
  editingItem: QuestionAnswer | null = null;

  qaForm: FormGroup;
  uploadedFile: File | null = null;
  loading = false;
  importLoading = false;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastrService,
    private dialog: MatDialog,
    private localizationService: LocalizationService
  ) {
    this.qaForm = this.fb.group({
      question: ['', [Validators.required, Validators.minLength(5)]],
      answer: ['', [Validators.required, Validators.minLength(5)]],
      tags: ['']
    });
  }

  ngOnInit() {
    this.initializeData();
  }

  initializeData() {
    if (this.formGroup && this.formGroup.get('qaData')) {
      const qaDataControl = this.formGroup.get('qaData');
      if (qaDataControl?.value) {
        this.qaList = [...qaDataControl.value];
        this.filterList();
      }
    }
  }

  onSearchChange() {
    this.filterList();
  }

  filterList() {
    if (!this.searchQuery.trim()) {
      this.filteredQaList = [...this.qaList];
    } else {
      const query = this.searchQuery.toLowerCase();
      this.filteredQaList = this.qaList.filter(item =>
        item.question.toLowerCase().includes(query) ||
        item.answer.toLowerCase().includes(query) ||
        (item.tags && item.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }
  }

  clearSearch() {
    this.searchQuery = '';
    this.filterList();
  }

  onTagKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
    }
  }

  addTag() {
    const tag = this.tagInput.trim();
    if (tag && !this.tags.includes(tag)) {
      this.tags.push(tag);
      this.tagInput = '';
      this.updateTagsInForm();
    }
  }

  removeTag(index: number) {
    this.tags.splice(index, 1);
    this.updateTagsInForm();
  }

  private updateTagsInForm() {
    this.qaForm.get('tags')?.setValue(this.tags.join(', '));
  }

  editQuestionAnswer(item: QuestionAnswer) {
    this.editingItem = item;
    this.tags = [...(item.tags || [])];
    this.qaForm.patchValue({
      question: item.question,
      answer: item.answer,
      tags: item.tags ? item.tags.join(', ') : ''
    });
    // Scroll to form
    document.querySelector('.card-header')?.scrollIntoView({ behavior: 'smooth' });
  }

  deleteQuestionAnswer(item: QuestionAnswer) {
    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: '400px',
      data: {
        fileName: item.question,
        modalTitle: this.localizationService.getLocalizationValue('QAInput.Messages.DeleteConfirmTitle'),
        modalQuestion: this.localizationService.getLocalizationValue('QAInput.Messages.DeleteConfirm')
      } as DeleteConfirmationData,
      disableClose: true
    });

    // Dialog component'ine referans al
    const dialogComponent = dialogRef.componentInstance;

    // Dialog'dan gelen deleteConfirmed event'ini dinle
    dialogComponent.deleteConfirmed.subscribe(() => {
      const index = this.qaList.findIndex(qa => qa.id === item.id);
      if (index !== -1) {
        this.qaList.splice(index, 1);
        this.updateFormGroup();
        this.qaDataChange.emit(this.qaList);
        this.filterList(); // Arama listesini güncelle
        this.toastService.success(
          this.localizationService.getLocalizationValue('QAInput.Messages.DeleteSuccess'),
          this.localizationService.getLocalizationValue('QAInput.Messages.Success')
        );

        // Eğer silinen item düzenleniyorsa formu temizle
        if (this.editingItem?.id === item.id) {
          this.resetForm();
        }

        // Dialog'u kapat
        dialogRef.close();
      }
    });
  }

  toggleActive(item: QuestionAnswer) {
    item.isActive = !item.isActive;
    this.updateFormGroup();
    this.qaDataChange.emit(this.qaList);
    const status = item.isActive ?
      this.localizationService.getLocalizationValue('QAInput.Messages.Active') :
      this.localizationService.getLocalizationValue('QAInput.Messages.Inactive');
    this.toastService.info(
      this.localizationService.getLocalizationValue('QAInput.Messages.StatusUpdated').replace('{{status}}', status),
      this.localizationService.getLocalizationValue('QAInput.Messages.StatusChanged')
    );
  }

  private generateId(): string {
    return 'qa_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  saveQuestionAnswer() {
    if (this.qaForm.valid) {
      this.loading = true;
      const formValue = this.qaForm.value;

      // Trim ve boş string kontrolü
      const trimmedQuestion = formValue.question?.trim() || '';
      const trimmedAnswer = formValue.answer?.trim() || '';

      if (!trimmedQuestion || !trimmedAnswer) {
        this.toastService.error(
          this.localizationService.getLocalizationValue('QAInput.Messages.FillAllFields'),
          this.localizationService.getLocalizationValue('QAInput.Messages.Error')
        );
        this.loading = false;
        return;
      }

      if (trimmedQuestion.length < 5 || trimmedAnswer.length < 5) {
        this.toastService.error(
          this.localizationService.getLocalizationValue('QAInput.Messages.MinLengthRequired'),
          this.localizationService.getLocalizationValue('QAInput.Messages.Error')
        );
        this.loading = false;
        return;
      }

      if (this.editingItem) {
        // Düzenleme modu
        const index = this.qaList.findIndex(item => item.id === this.editingItem!.id);
        if (index !== -1) {
          // Duplicate kontrolü (kendi hariç)
          const duplicateIndex = this.qaList.findIndex((qa, i) =>
            i !== index && qa.question.toLowerCase() === trimmedQuestion.toLowerCase()
          );

          if (duplicateIndex !== -1) {
            this.toastService.error(
              this.localizationService.getLocalizationValue('QAInput.Messages.QuestionExists'),
              this.localizationService.getLocalizationValue('QAInput.Messages.Error')
            );
            this.loading = false;
            return;
          }

          this.qaList[index] = {
            ...this.editingItem,
            question: trimmedQuestion,
            answer: trimmedAnswer,
            tags: [...this.tags]
          };
          this.toastService.success(
            this.localizationService.getLocalizationValue('QAInput.Messages.QAUpdated'),
            this.localizationService.getLocalizationValue('QAInput.Messages.Success')
          );
        }
      } else {
        // Yeni ekleme modu
        const newQA: QuestionAnswer = {
          id: this.generateId(), // Benzersiz ID oluştur
          question: trimmedQuestion,
          answer: trimmedAnswer,
          isActive: true,
          tags: [...this.tags],
          createdAt: new Date()
        };

        // Duplicate kontrolü
        if (this.qaList.some(qa => qa.question.toLowerCase() === newQA.question.toLowerCase())) {
          this.toastService.error(
            this.localizationService.getLocalizationValue('QAInput.Messages.QuestionExists'),
            this.localizationService.getLocalizationValue('QAInput.Messages.Error')
          );
          this.loading = false;
          return;
        }

        this.qaList.push(newQA);
        this.toastService.success(
          this.localizationService.getLocalizationValue('QAInput.Messages.QAAdded'),
          this.localizationService.getLocalizationValue('QAInput.Messages.Success')
        );
      }

      this.updateFormGroup();
      this.qaDataChange.emit(this.qaList);
      this.filterList(); // Arama listesini güncelle
      this.resetForm();
      this.loading = false;
    } else {
      this.toastService.error(
        this.localizationService.getLocalizationValue('QAInput.Messages.FillAllFields'),
        this.localizationService.getLocalizationValue('QAInput.Messages.Error')
      );
    }
  }

  resetForm() {
    this.qaForm.reset();
    this.qaForm.markAsUntouched();
    this.tags = [];
    this.tagInput = '';
    this.editingItem = null;
  }

  trackByFn(index: number, item: QuestionAnswer): string {
    return item.id || `${item.question}_${item.createdAt?.getTime()}_${index}`;
  }

  openImportModal() {
    this.showImportModal = true;
    this.uploadedFile = null;
  }

  closeImportModal() {
    this.showImportModal = false;
    this.uploadedFile = null;
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.uploadedFile = file;
      this.fileUploadEvent.emit(file);
    }
  }

  importFromExcel() {
    if (!this.uploadedFile) {
      this.toastService.error(
        this.localizationService.getLocalizationValue('QAInput.Messages.SelectFile'),
        this.localizationService.getLocalizationValue('QAInput.Messages.Error')
      );
      return;
    }

    this.importLoading = true;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: ''
        }) as any[][];

        if (jsonData.length < 2) {
          this.toastService.error(
            this.localizationService.getLocalizationValue('QAInput.Messages.InsufficientData'),
            this.localizationService.getLocalizationValue('QAInput.Messages.Error')
          );
          this.importLoading = false;
          return;
        }

        const headers = jsonData[0];
        const questionIndex = this.findColumnIndex(headers, ['soru', 'question', 'sual']);
        const answerIndex = this.findColumnIndex(headers, ['cevap', 'answer', 'cavab', 'yanıt']);

        if (questionIndex === -1 || answerIndex === -1) {
          this.toastService.error(
            this.localizationService.getLocalizationValue('QAInput.Messages.ColumnsNotFound'),
            this.localizationService.getLocalizationValue('QAInput.Messages.Error')
          );
          this.importLoading = false;
          return;
        }

        const newQAList: QuestionAnswer[] = [];
        let successCount = 0;
        let skipCount = 0;

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          const question = row[questionIndex]?.toString().trim();
          const answer = row[answerIndex]?.toString().trim();

          if (question && answer && question.length >= 5 && answer.length >= 5) {
            if (!this.qaList.some(qa => qa.question.toLowerCase() === question.toLowerCase()) &&
                !newQAList.some(qa => qa.question.toLowerCase() === question.toLowerCase())) {

              newQAList.push({
                id: this.generateId(), // Benzersiz ID oluştur
                question,
                answer,
                isActive: true,
                tags: [],
                createdAt: new Date()
              });
              successCount++;
            } else {
              skipCount++;
            }
          }
        }

        this.qaList.push(...newQAList);
        this.updateFormGroup();
        this.qaDataChange.emit(this.qaList);
        this.filterList(); // Arama listesini güncelle

        let message = this.localizationService.getLocalizationValue('QAInput.Messages.ImportSuccess').replace('{{count}}', successCount.toString());
        if (skipCount > 0) {
          message += ' ' + this.localizationService.getLocalizationValue('QAInput.Messages.DuplicatesSkipped').replace('{{count}}', skipCount.toString());
        }
        this.toastService.success(
          message,
          this.localizationService.getLocalizationValue('QAInput.Messages.Success')
        );

        this.closeImportModal();

        // Import sonrası kullanıcıyı listeye yönlendir
        setTimeout(() => {
          const listElement = document.querySelector('[style*="max-height: 500px"]');
          if (listElement && newQAList.length > 0) {
            listElement.scrollTop = listElement.scrollHeight;
            this.toastService.info(
              this.localizationService.getLocalizationValue('QAInput.Messages.NewQAsAdded').replace('{{count}}', successCount.toString()),
              this.localizationService.getLocalizationValue('QAInput.Messages.Info')
            );
          }
        }, 500);

      } catch (error) {
        this.toastService.error(
          this.localizationService.getLocalizationValue('QAInput.Messages.FileReadError'),
          this.localizationService.getLocalizationValue('QAInput.Messages.Error')
        );
        console.error('Excel reading error:', error);
      } finally {
        this.importLoading = false;
      }
    };

    reader.readAsArrayBuffer(this.uploadedFile);
  }

  private findColumnIndex(headers: any[], possibleNames: string[]): number {
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]?.toString().toLowerCase().trim();
      if (possibleNames.some(name => header.includes(name))) {
        return i;
      }
    }
    return -1;
  }

  private updateFormGroup() {
    if (this.formGroup) {
      const qaDataControl = this.formGroup.get('qaData');
      if (qaDataControl) {
        qaDataControl.setValue(this.qaList);
        qaDataControl.updateValueAndValidity();
      }
    }
  }

  get questionControl() {
    return this.qaForm.get('question');
  }

  get answerControl() {
    return this.qaForm.get('answer');
  }

  get totalQuestionsText(): string {
    return this.localizationService.getLocalizationValue('QAInput.TotalQuestions').replace('{{count}}', this.qaList.length.toString());
  }

  get searchResultsText(): string {
    return this.localizationService.getLocalizationValue('QAInput.SearchResults')
      .replace('{{filtered}}', this.filteredQaList.length.toString())
      .replace('{{total}}', this.qaList.length.toString());
  }

  get noResultsForText(): string {
    return this.localizationService.getLocalizationValue('QAInput.NoResultsFor').replace('{{query}}', `"${this.searchQuery}"`);
  }

  get qAsAvailableText(): string {
    return this.localizationService.getLocalizationValue('QAInput.QAsAvailable').replace('{{count}}', this.qaList.length.toString());
  }

  get searchResultsCountText(): string {
    return this.localizationService.getLocalizationValue('QAInput.SearchResultsCount').replace('{{count}}', this.filteredQaList.length.toString());
  }
}
